<template>
  <div>
    <div ref="mapContainer" class="map-container mb-4"></div>
    <section id="" class="skiddish flex flex-col justify-content-center" :style="{ visibility: isGatedVisible ? 'visible' : 'hidden' }">
      <div class="card mx-0 mb-2 p-2">
        <p class="card-title">Map Modes</p>
        <div class="flex flex-row gap-2">
          <button class="btn" v-on:click="changeMapStyle('standard')">Standard</button>
          <button class="btn" v-on:click="changeMapStyle('standard-satellite')">Satellite</button>
          <button class="btn" v-on:click="changeMapStyle('outdoors-v12')">Outdoors</button>
          <button class="btn" v-on:click="changeMapStyle('streets-v12')">Streets</button>
          <button class="btn" v-on:click="changeMapStyle('navigation-day-v1')">Navigation - Daytime</button>
          <button class="btn" v-on:click="changeMapStyle('navigation-night-v1')">Navigation - Nighttime</button>
        </div>
      </div>
      <div class="card mx-0 mb-4 p-2">
        <h2 class="text-lg">Gateway Location</h2>
        <p class="mb-3 max-w-prose" v-if="canHazLocation">
          You can drag-and-drop the pin using the map to update the location of your sensor.
          If you already know the coordinates you would like to use for your sensor, you can enter them below and click "Update Location".
        </p>
        <div class="flex flex-col">
          <form key="location-form" id="gateway-location-form" class="flex flex-col justify-content-center" v-if="canHazLocation">
            <label for="gateway-latitude">Latitude:</label>
            <input type="text" v-model="gatewayLat" name="gateway-latitude" id="gateway-latitude" class="bg-white text-black pl-2 max-w-1/2 rounded" />
            <br />
            <label for="gateway-longitude">Longitude:</label>
            <input type="text" v-model="gatewayLng" name="gateway-longitude" id="gateway-longitude" class="bg-white text-black pl-2 max-w-1/2 rounded" />
            <br />
            <div class="mb-2">
              <input type="checkbox" id="update-location-checkbox" name="update-location-checkbox" v-model="updateChecked" />
              <label for="update-location-checkbox" class="pl-2 text-sm">Yes, I want to update this location with the values in the form above.</label>
            </div>
            <div class="flex flex-row gap-2">
              <button @click.prevent="updateGatewayLocation" :disabled="!updateChecked" class="btn">Update Location</button>
            </div>
          </form>
          <form key="add-location-form" id="gateway-location-add-form" class="" v-else>
            <!-- Else we should put in a form to add a location for this gateway -->
            <p>This gateway does not appear to have a location registered.
              If you would like to add one, you can use the form below.</p>
          </form>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import "../../../assets/tailwind.css";
import {ref, shallowRef, onMounted, onBeforeUnmount, computed} from 'vue';
import { useRoute } from 'vue-router';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useStore } from 'vuex';
import { format } from 'date-fns';

const gatewayAttributeList = computed(() => store.state.apgList.List.gateway_attribute);

// Set up the access token and initial map coordinates
const accessToken = 'pk.eyJ1IjoiYmVub2l0ZHVyZXQiLCJhIjoiY2w2OXhjeHMyMTZ0ajNpbzVhM2MwaHlteiJ9.4R_hyzrZFN7bZrix5X_Nnw';
const coordinates = [-96.22526430339026, 38.80948251839888];

// References for the map container, map instance, and markers
const mapContainer = ref(null);
const map = shallowRef(null);
const markers = ref([]);

// Access the Vuex store to retrieve the gateway list
const store = useStore();
const route = useRoute();

// toss complex determination patterns for placing the marker and if we have a location. If we have lat+lng, we _have_ a location.
const latitude = gatewayAttributeList.value.find(item => item.attribute_name === "telemetryLatitude")?.attribute_value;
const longitude = gatewayAttributeList.value.find(item => item.attribute_name === "telemetryLongitude")?.attribute_value;
const ownerId = gatewayAttributeList.value.find(item => item.owner_id)?.owner_id;

const gatewayList = computed(() => store.state.apgList?.List?.gateway_GPS_Location);
const currentGatewayId = computed(() => route.params.id || route.query.gatewayId || store.state.MyView?.selected?.gateway?.id || store.state.gateway?.id);
const gatewayName = computed(() => {
  // let gateway = store.getters['MyView/getGateway'](currentGatewayId.value);
  let gateway = store.getters['MyView/getGateway'](ownerId);
  // Fallback for single-gateway view if ID lookup fails
  if (!gateway && gatewayList.value?.length === 1) {
    gateway = gatewayList.value[0];
  }
  return gateway ? gateway.label : 'Unknown Gateway';
});

const isGatedVisible = computed(() => store.state.ui?.isGatedVisible ?? false);
const canHazLocation = ref(false);
const gatewayLat = ref('');
const gatewayLng = ref('');
const updateChecked = ref(false);

onMounted(() => {
  mapboxgl.accessToken = accessToken;
  // Initialize the Mapbox map
  map.value = new mapboxgl.Map({
    container: mapContainer.value,
    style: 'mapbox://styles/mapbox/standard',
    center: coordinates,
    zoom: 3
  });
  const nav = new mapboxgl.NavigationControl({
    showCompass: true,
    showZoom: true,
    visualizePitch: true
  });
  map.value.addControl(nav, 'top-left');
  // Add markers once the map has loaded
  map.value.on('load', () => {
    addMarkers();
    resizeMap(); // Resize the map to fix any layout issues
  });
});

onBeforeUnmount(() => {
  // Note: We intentionally do NOT remove the map here when using keep-alive
  // The map instance is preserved by keep-alive, and removing it would
  // destroy the map when switching tabs. The map will be properly cleaned
  // up when the parent component is unmounted.
  // If you need to force cleanup in specific scenarios, call map.value.remove() manually.
});

const resizeMap = () => {
  if (map.value) map.value.resize();
};

// eslint-disable-next-line no-undef
defineExpose({ resizeMap });

function changeMapStyle(styleName) {
  styleName = styleName ?? 'standard';
  map.value.setStyle('mapbox://styles/mapbox/'+styleName);
}

function updateGatewayLocation() {
  updateMarkerLatLng({lat: gatewayLat.value, lng: gatewayLng.value}, currentGatewayId.value);
}

// updateMarkerLatLng - When using drag-and-drop on a pin, this saves the location to the database
function updateMarkerLatLng(latLng, gatewayId) {
  // Use the provided ID, or fall back to the gatewayId or current gateway ID if ownerId is missing
  const idToUse = ownerId ?? (gatewayId || currentGatewayId.value);

  if (!idToUse) {
    console.error("updateMarkerLatLng: No gateway ID provided or found in route/store.");
    return;
  }

  // Ensure coordinates are numbers (especially when coming from form inputs)
  const lat = Number(latLng.lat);
  const lng = Number(latLng.lng);

  store.dispatch('user/upsertAttribute', {
    attribute_value: lng,
    attribute_info_id: 9136,
    owner_id: idToUse
  });
  store.dispatch('user/upsertAttribute', {
    attribute_value: lat,
    attribute_info_id: 9137,
    owner_id: idToUse
  });

  // Update local refs to keep the UI input fields in sync with the map pin position
  gatewayLat.value = lat;
  gatewayLng.value = lng;

  // Find the marker for this gateway and update its position on the map
  let marker = markers.value.find(m => String(m.gatewayId) === String(idToUse));

  // Fallback: If we can't find it by ID but there is only one marker, use that one
  if (!marker && markers.value.length === 1) {
    marker = markers.value[0];
  }

  if (marker) {
    marker.setLngLat([lng, lat]);
    map.value.flyTo({ center: [lng, lat], zoom: 17 });
  } else {
    console.error(`Marker not found for gateway ID: ${idToUse}`);
  }
}

// Function to add markers to the map with formatted last-update time
const addMarkers = () => {
  // Remove any existing markers
  markers.value.forEach((marker) => marker.remove());
  markers.value = [];

  if(latitude !== null || longitude !== null) {
    canHazLocation.value = true;
    const lastUpdateReceived = gatewayAttributeList.value.find(item => item.attribute_name === "last_heard_from")?.attribute_value;
    const formattedDate = lastUpdateReceived ? format(new Date(lastUpdateReceived), 'MMM do yy, h:mm:ss a') : '';
    const customMarker = document.createElement('div');
    customMarker.classList.add('custom-marker');
    const marker = new mapboxgl.Marker({
      draggable: true,
      color: "#FF9900",
      element: customMarker,
    }).setLngLat([longitude, latitude])
      .setPopup(new mapboxgl.Popup({
        offset: 20,
        className: ''
      }).setHTML(`<p><span class="quantify">Gateway</span>:<br />${gatewayName.value}</p><p><span class="quantify">Last Update Received</span>:<br />${formattedDate}</p>`))
      .addTo(map.value);

    // Tag the marker with the device ID so we can find it later - note this is _not_ using the ownerId, it is using the **Device ID attribute_value**
    marker.gatewayId = gatewayAttributeList.value.find(item => item.attribute_name === "deviceId")?.attribute_value;
    marker.on('dragend', function() {updateMarkerLatLng(marker.getLngLat(), marker.gatewayId)});
    markers.value.push(marker);

    map.value.flyTo({
      center: [longitude, latitude],
      zoom: 17, 
      speed: 1.5,
      essential: true
    });
    gatewayLat.value = latitude;
    gatewayLng.value = longitude;
  } else {
    canHazLocation.value = false;
  }
};
</script>
